import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Plus, Download, Package, AlertTriangle, CheckCircle, Pencil, Trash2, UtensilsCrossed, AlertCircle, Calendar, CalendarPlus, FileText } from "lucide-react";
import jsPDF from "jspdf";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function CateringDashboard() {
  const { toast } = useToast();
  
  const [isAddStockDialogOpen, setIsAddStockDialogOpen] = useState(false);
  const [isAddMenuItemDialogOpen, setIsAddMenuItemDialogOpen] = useState(false);
  const [isEditStockDialogOpen, setIsEditStockDialogOpen] = useState(false);
  const [isEditMenuItemDialogOpen, setIsEditMenuItemDialogOpen] = useState(false);
  const [isViewEventDialogOpen, setIsViewEventDialogOpen] = useState(false);
  const [isReturnStockDialogOpen, setIsReturnStockDialogOpen] = useState(false);
  const [selectedStockItem, setSelectedStockItem] = useState<any>(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState<any>(null);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [returnAmount, setReturnAmount] = useState("");
  const [isAssignStockDialogOpen, setIsAssignStockDialogOpen] = useState(false);
  const [isEditStockAssignmentDialogOpen, setIsEditStockAssignmentDialogOpen] = useState(false);
  const [selectedMenuItems, setSelectedMenuItems] = useState<number[]>([]);
  const [assignQuantity, setAssignQuantity] = useState("");
  const [selectedIcon, setSelectedIcon] = useState<string>("🍔");
  const [editSelectedIcon, setEditSelectedIcon] = useState<string>("");
  
  // Data state
  const [stock, setStock] = useState([
    { id: 1, item: "Hot Dog Buns", batch: "B001", quantity: 50, expiry: "2025-02-15", status: "ok" },
    { id: 2, item: "Burger Patties", batch: "B002", quantity: 8, expiry: "2025-01-28", status: "low" },
    { id: 3, item: "Vegetable Samosas", batch: "B003", quantity: 30, expiry: "2025-01-25", status: "expiring" },
    { id: 4, item: "Soft Drinks (Cans)", batch: "B004", quantity: 120, expiry: "2025-06-01", status: "ok" },
  ]);

  const [menuItems, setMenuItems] = useState([
    { id: 1, name: "Classic Hot Dog", price: "£2.50", allergens: ["GF"], color: "red", icon: "🌭" },
    { id: 2, name: "Veggie Burger", price: "£3.00", allergens: ["V", "VE"], color: "green", icon: "🍔" },
    { id: 3, name: "Cheese Pizza Slice", price: "£2.00", allergens: ["V"], color: "orange", icon: "🍕" },
  ]);

  const [events, setEvents] = useState([
    { id: 1, name: "Summer Fair 2024", date: "15th June 2024", assignedItems: "Hot Dogs, Burgers, Drinks" },
    { id: 2, name: "Christmas Market 2024", date: "20th December 2024", assignedItems: "Pizza, Cakes, Coffee" },
    { id: 3, name: "Sports Day 2024", date: "10th July 2024", assignedItems: "Sandwiches, Water, Fruits" },
  ]);
  
  // Mock assigned stock data for the event
  const [eventStockAssignments, setEventStockAssignments] = useState([
    { id: 1, item: "Hot Dog Buns", quantity: 50 },
    { id: 2, item: "Burger Patties", quantity: 30 },
    { id: 3, item: "Soft Drinks (Cans)", quantity: 60 },
  ]);

  const cateringIcons = [
    { name: "apple", icon: "🍎", color: "text-red-500" },
    { name: "burger", icon: "🍔", color: "text-orange-500" },
    { name: "pizza", icon: "🍕", color: "text-yellow-500" },
    { name: "hotdog", icon: "🌭", color: "text-amber-500" },
    { name: "fries", icon: "🍟", color: "text-yellow-600" },
    { name: "taco", icon: "🌮", color: "text-orange-600" },
    { name: "sandwich", icon: "🥪", color: "text-yellow-700" },
    { name: "salad", icon: "🥗", color: "text-green-500" },
    { name: "soup", icon: "🍲", color: "text-orange-400" },
    { name: "cake", icon: "🍰", color: "text-pink-500" },
    { name: "cookie", icon: "🍪", color: "text-amber-600" },
    { name: "cupcake", icon: "🧁", color: "text-pink-400" },
    { name: "donut", icon: "🍩", color: "text-pink-600" },
    { name: "icecream", icon: "🍦", color: "text-blue-300" },
    { name: "coffee", icon: "☕", color: "text-amber-700" },
    { name: "tea", icon: "🫖", color: "text-green-600" },
    { name: "juice", icon: "🧃", color: "text-orange-400" },
    { name: "water", icon: "💧", color: "text-blue-500" },
    { name: "soda", icon: "🥤", color: "text-red-400" },
    { name: "milk", icon: "🥛", color: "text-blue-100" },
  ];

  const handleAddStock = (e: React.FormEvent) => {
    e.preventDefault();
    setIsAddStockDialogOpen(false);
    toast({ title: "Stock Added", description: "New stock item has been added successfully" });
  };

  const handleEditStock = (e: React.FormEvent) => {
    e.preventDefault();
    setIsEditStockDialogOpen(false);
    toast({ title: "Stock Updated", description: "Stock item has been updated successfully" });
  };

  const handleDeleteStock = (itemId: number) => {
    setStock(prev => prev.filter(item => item.id !== itemId));
    toast({ title: "Stock Deleted", description: "Item has been removed from inventory" });
  };

  const handleAddMenuItem = (e: React.FormEvent) => {
    e.preventDefault();
    setIsAddMenuItemDialogOpen(false);
    setSelectedIcon("🍔"); // Reset to default
    toast({ title: "Menu Item Added", description: "New menu item has been added successfully" });
  };

  const handleEditMenuItem = () => {
    setIsEditMenuItemDialogOpen(false);
    setEditSelectedIcon(""); // Reset
    toast({ title: "Menu Item Updated", description: "Menu item has been updated successfully" });
  };

  const handleOpenEditMenuItem = (item: any) => {
    setSelectedMenuItem(item);
    setEditSelectedIcon(item.icon);
    setIsEditMenuItemDialogOpen(true);
  };

  const handleDownloadMenu = () => {
    if (selectedMenuItems.length === 0) {
      toast({ title: "No Items Selected", description: "Please select menu items to download", variant: "destructive" });
      return;
    }
    toast({ title: "Download Menu", description: `Downloading menu with ${selectedMenuItems.length} items` });
  };

  const handlePrintPricingSheet = (item: any) => {
    const doc = new jsPDF({ orientation: 'landscape' });
    
    doc.setFontSize(24);
    doc.text(item.name, 148, 40, { align: 'center' });
    
    doc.setFontSize(60);
    doc.text(item.icon, 148, 90, { align: 'center' });
    
    doc.setFontSize(32);
    doc.text(item.price, 148, 130, { align: 'center' });
    
    doc.setFontSize(14);
    doc.text('Allergens:', 148, 160, { align: 'center' });
    doc.setFontSize(12);
    doc.text(item.allergens.join(', '), 148, 170, { align: 'center' });
    
    doc.save(`${item.name.replace(/\s+/g, '-')}-pricing-sheet.pdf`);
    toast({ title: "Pricing Sheet Generated", description: `PDF created for ${item.name}` });
  };

  const handleDeleteMenuItem = (itemId: number) => {
    setMenuItems(prev => prev.filter(item => item.id !== itemId));
    setSelectedMenuItems(prev => prev.filter(id => id !== itemId));
    toast({ title: "Menu Item Deleted", description: "Item has been removed from the menu" });
  };

  const handleAssignStock = () => {
    if (!assignQuantity || parseFloat(assignQuantity) <= 0) {
      toast({ title: "Invalid Quantity", description: "Please enter a valid quantity", variant: "destructive" });
      return;
    }
    toast({ title: "Stock Assigned", description: `${assignQuantity} items assigned to event` });
    setIsAssignStockDialogOpen(false);
    setAssignQuantity("");
  };

  const handleDeleteEvent = (eventId: number) => {
    setEvents(prev => prev.filter(event => event.id !== eventId));
    toast({ title: "Event Deleted", description: "Event has been removed" });
  };

  const handleEditStockAssignment = (event: any) => {
    setSelectedEvent(event);
    setIsEditStockAssignmentDialogOpen(true);
  };

  const handleUpdateStockQuantity = (itemId: number, newQuantity: number) => {
    setEventStockAssignments(prev => 
      prev.map(item => item.id === itemId ? { ...item, quantity: newQuantity } : item)
    );
  };

  const handleSaveStockAssignments = () => {
    toast({ title: "Stock Assignments Updated", description: "Event stock quantities have been updated" });
    setIsEditStockAssignmentDialogOpen(false);
  };

  const toggleMenuItemSelection = (itemId: number) => {
    setSelectedMenuItems(prev => 
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  const handleViewEventDetails = (event: any) => {
    setSelectedEvent(event);
    setIsViewEventDialogOpen(true);
  };

  const handleReturnStock = (event: any) => {
    setSelectedEvent(event);
    setIsReturnStockDialogOpen(true);
  };

  const handleProcessReturn = () => {
    if (!returnAmount || parseFloat(returnAmount) <= 0) {
      toast({ title: "Invalid Amount", description: "Please enter a valid return amount", variant: "destructive" });
      return;
    }
    toast({ title: "Stock Returned", description: `${returnAmount} items returned to oldest batch for ${selectedEvent?.name}` });
    setIsReturnStockDialogOpen(false);
    setReturnAmount("");
  };

  const allergenBadgeColor = (allergen: string) => {
    switch (allergen) {
      case "V": return "bg-green-100 text-green-800";
      case "VE": return "bg-emerald-100 text-emerald-800";
      case "GF": return "bg-blue-100 text-blue-800";
      case "DF": return "bg-purple-100 text-purple-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-4xl font-bold text-foreground">
          Catering Dashboard
        </h1>
        <p className="text-muted-foreground mt-2">
          Manage catering stock, menu items, and event assignments
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Stock Items</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stock.length}
            </div>
            <p className="text-sm text-muted-foreground">
              Items currently in stock
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Menu Items</CardTitle>
            <UtensilsCrossed className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {menuItems.length}
            </div>
            <p className="text-sm text-muted-foreground">
              Items available on the menu
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Upcoming Events</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              3
            </div>
            <p className="text-sm text-muted-foreground">
              Events scheduled this month
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expiring Soon</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">
              1
            </div>
            <p className="text-sm text-muted-foreground">
              Stock items expiring this week
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="stock" className="w-full">
        <TabsList>
          <TabsTrigger value="stock">Stock Management</TabsTrigger>
          <TabsTrigger value="menu">Menu & Pricing</TabsTrigger>
          <TabsTrigger value="events">Event Assignments</TabsTrigger>
        </TabsList>

        <TabsContent value="stock" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Stock Inventory</CardTitle>
                  <CardDescription>Track stock by batch and expiry date</CardDescription>
                </div>
                <Dialog open={isAddStockDialogOpen} onOpenChange={setIsAddStockDialogOpen}>
                  <DialogTrigger asChild>
                    <Button><Plus className="h-4 w-4 mr-2" />Add Stock</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Stock Item</DialogTitle>
                      <DialogDescription>Add a new item to the stock inventory</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddStock} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="item-name">Item Name</Label>
                        <Input id="item-name" required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="batch">Batch Number</Label>
                        <Input id="batch" required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="quantity">Quantity</Label>
                        <Input id="quantity" type="number" required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="expiry">Expiry Date</Label>
                        <Input id="expiry" type="date" required />
                      </div>
                      <div className="flex justify-end gap-2">
                        <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
                        <Button type="submit">Add Stock</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Batch</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Expiry</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stock.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.item}</TableCell>
                      <TableCell className="font-mono text-sm">{item.batch}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.expiry}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          item.status === "ok" ? "bg-green-100 text-green-800" :
                          item.status === "low" ? "bg-yellow-100 text-yellow-800" :
                          "bg-red-100 text-red-800"
                        }`}>
                          {item.status === "ok" ? "Good" : item.status === "low" ? "Low Stock" : "Expiring Soon"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedStockItem(item); setIsAssignStockDialogOpen(true); }}>
                            <CalendarPlus className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedStockItem(item); setIsEditStockDialogOpen(true); }}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteStock(item.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="menu" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Menu Items</CardTitle>
                  <CardDescription>Manage menu items and pricing</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={handleDownloadMenu}
                    disabled={selectedMenuItems.length === 0}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Menu
                  </Button>
                  <Dialog open={isAddMenuItemDialogOpen} onOpenChange={setIsAddMenuItemDialogOpen}>
                    <DialogTrigger asChild>
                      <Button><Plus className="h-4 w-4 mr-2" />Add Item</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Add Menu Item</DialogTitle>
                        <DialogDescription>Add a new item to the menu</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleAddMenuItem} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="item-name">Item Name</Label>
                          <Input id="item-name" required />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="item-price">Price</Label>
                          <Input id="item-price" type="number" step="0.01" required />
                        </div>
                        <div className="space-y-2">
                          <Label>Allergens</Label>
                          <div className="flex gap-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox id="allergen-v" />
                              <Label htmlFor="allergen-v">V</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox id="allergen-ve" />
                              <Label htmlFor="allergen-ve">VE</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox id="allergen-gf" />
                              <Label htmlFor="allergen-gf">GF</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox id="allergen-df" />
                              <Label htmlFor="allergen-df">DF</Label>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Icon</Label>
                          <div className="grid grid-cols-10 gap-2 max-h-32 overflow-y-auto p-2 border rounded-md">
                            {cateringIcons.map(icon => (
                              <Button 
                                key={icon.name} 
                                variant={selectedIcon === icon.icon ? "default" : "ghost"}
                                size="icon" 
                                type="button"
                                onClick={() => setSelectedIcon(icon.icon)}
                                className={selectedIcon === icon.icon ? "ring-2 ring-primary" : ""}
                              >
                                <span className="text-2xl">{icon.icon}</span>
                              </Button>
                            ))}
                          </div>
                          <p className="text-sm text-muted-foreground">Selected: {selectedIcon}</p>
                        </div>
                        <div className="flex justify-end gap-2">
                          <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
                          <Button type="submit">Add Item</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Allergens</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {menuItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Checkbox 
                          checked={selectedMenuItems.includes(item.id)}
                          onCheckedChange={() => toggleMenuItemSelection(item.id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{item.icon}</span>
                          {item.name}
                        </div>
                      </TableCell>
                      <TableCell>{item.price}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {item.allergens.map(allergen => (
                            <Badge key={allergen} className={allergenBadgeColor(allergen)}>{allergen}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenEditMenuItem(item)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handlePrintPricingSheet(item)}>
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteMenuItem(item.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Event Assignments</CardTitle>
              <CardDescription>Assign menu items and track stock usage for events</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {events.map(event => (
                  <Card key={event.id} className="bg-muted/50">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle>{event.name}</CardTitle>
                          <CardDescription>{event.date}</CardDescription>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEditStockAssignment(event)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteEvent(event.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        Assigned Items: {event.assignedItems}
                      </p>
                      <div className="flex justify-between items-center">
                        <Button variant="outline" size="sm" onClick={() => handleViewEventDetails(event)}>
                          View Details
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleReturnStock(event)}>
                          <Package className="h-4 w-4 mr-2" />
                          Return Stock
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={isEditStockDialogOpen} onOpenChange={setIsEditStockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Stock Item</DialogTitle>
            <DialogDescription>Update details for the selected stock item</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditStock} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-item-name">Item Name</Label>
              <Input id="edit-item-name" defaultValue={selectedStockItem?.item} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-batch">Batch Number</Label>
              <Input id="edit-batch" defaultValue={selectedStockItem?.batch} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-quantity">Quantity</Label>
              <Input id="edit-quantity" type="number" defaultValue={selectedStockItem?.quantity} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-expiry">Expiry Date</Label>
              <Input id="edit-expiry" type="date" defaultValue={selectedStockItem?.expiry} required />
            </div>
            <div className="flex justify-end gap-2">
              <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
              <Button type="submit">Update Stock</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      <Dialog open={isEditMenuItemDialogOpen} onOpenChange={setIsEditMenuItemDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Menu Item</DialogTitle>
            <DialogDescription>Edit details for the selected menu item</DialogDescription>
          </DialogHeader>
          <form onSubmit={() => {}} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-item-name">Item Name</Label>
              <Input id="edit-item-name" defaultValue={selectedMenuItem?.name} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-item-price">Price</Label>
              <Input id="edit-item-price" type="number" step="0.01" defaultValue={selectedMenuItem?.price} required />
            </div>
            <div className="space-y-2">
              <Label>Allergens</Label>
              <div className="flex gap-2">
                <div className="flex items-center space-x-2">
                  <Checkbox id="edit-allergen-v" />
                  <Label htmlFor="edit-allergen-v">V</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="edit-allergen-ve" />
                  <Label htmlFor="edit-allergen-ve">VE</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="edit-allergen-gf" />
                  <Label htmlFor="edit-allergen-gf">GF</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="edit-allergen-df" />
                  <Label htmlFor="edit-allergen-df">DF</Label>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="grid grid-cols-10 gap-2 max-h-32 overflow-y-auto p-2 border rounded-md">
                {cateringIcons.map(icon => (
                  <Button 
                    key={icon.name} 
                    variant={editSelectedIcon === icon.icon ? "default" : "ghost"}
                    size="icon" 
                    type="button"
                    onClick={() => setEditSelectedIcon(icon.icon)}
                    className={editSelectedIcon === icon.icon ? "ring-2 ring-primary" : ""}
                  >
                    <span className="text-2xl">{icon.icon}</span>
                  </Button>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">Selected: {editSelectedIcon}</p>
            </div>
            <div className="flex justify-end gap-2">
              <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
              <Button type="submit" onClick={handleEditMenuItem}>Update Item</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      <Dialog open={isViewEventDialogOpen} onOpenChange={setIsViewEventDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Event Details</DialogTitle>
            <DialogDescription>View details for the selected event</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p>Event Name: Summer Fair 2024</p>
            <p>Date: 15th June 2024</p>
            <p>Assigned Items: Hot Dogs, Burgers, Drinks</p>
            <DialogClose asChild>
              <Button type="button">Close</Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
      
      <Dialog open={isReturnStockDialogOpen} onOpenChange={setIsReturnStockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return Stock</DialogTitle>
            <DialogDescription>Return stock items to the inventory (oldest batch first)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p>Event: Summer Fair 2024</p>
            <div className="space-y-2">
              <Label htmlFor="return-amount">Amount to Return</Label>
              <Input
                id="return-amount"
                type="number"
                value={returnAmount}
                onChange={(e) => setReturnAmount(e.target.value)}
                placeholder="Enter amount"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsReturnStockDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleProcessReturn}>Process Return</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isAssignStockDialogOpen} onOpenChange={setIsAssignStockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Stock to Event</DialogTitle>
            <DialogDescription>Select an event and quantity to assign stock</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="font-medium">Item: {selectedStockItem?.item}</p>
            <div className="space-y-2">
              <Label htmlFor="event-select">Select Event</Label>
              <Select>
                <SelectTrigger id="event-select">
                  <SelectValue placeholder="Choose an event" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="event1">Summer Fair 2024</SelectItem>
                  <SelectItem value="event2">Christmas Market 2024</SelectItem>
                  <SelectItem value="event3">Sports Day 2024</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="assign-quantity">Quantity to Assign</Label>
              <Input
                id="assign-quantity"
                type="number"
                value={assignQuantity}
                onChange={(e) => setAssignQuantity(e.target.value)}
                placeholder="Enter quantity"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsAssignStockDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAssignStock}>Assign Stock</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditStockAssignmentDialogOpen} onOpenChange={setIsEditStockAssignmentDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Stock Assignments</DialogTitle>
            <DialogDescription>Update stock quantities assigned to this event</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="font-medium">Event: Summer Fair 2024</p>
            <div className="space-y-3">
              {eventStockAssignments.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="font-medium">{item.item}</span>
                  <div className="flex items-center gap-2">
                    <Label htmlFor={`quantity-${item.id}`} className="text-sm text-muted-foreground">Quantity:</Label>
                    <Input
                      id={`quantity-${item.id}`}
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleUpdateStockQuantity(item.id, parseInt(e.target.value) || 0)}
                      className="w-24"
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsEditStockAssignmentDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveStockAssignments}>Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
